/**
 * Rich text with a small toolbar: headings, bold, italic, lists, a link.
 * Shared by the policy pages, the FAQ and the landing-page copy.
 *
 * The toolbar offers only what the backend keeps. It cleans every body on
 * save down to headings, paragraphs, bold, italic, lists and links to our
 * own site, so a button for colour or alignment here would let an admin
 * format something that silently disappears after saving. Heading 1 is
 * left out on purpose: the page prints its own title as the h1, and a
 * second one costs the page its outline in Google.
 *
 * `headings` is the levels a page may use (the landing copy stops at h3,
 * the policies go to h4). `onLink`, when given, replaces Quill's own
 * "type a URL" prompt: it is called with an `insert({ href, text })`
 * function and is expected to open a picker; the policies keep the prompt.
 */
import React, { useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./RichTextField.css";

const FORMATS = ["header", "bold", "italic", "list", "bullet", "link"];

const defaultLinkHandler = ReactQuill.Quill.import("modules/toolbar").DEFAULTS.handlers.link;

/**
 * What a picker gets: the words selected when the link button was pressed
 * become the link, or the page's name is inserted when nothing was.
 * Both write with the "user" source, so the field reports the change.
 */
const inserterFor = (quill) => {
  const range = quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 };
  return ({ href, text }) => {
    quill.focus();
    if (range.length > 0) {
      quill.setSelection(range.index, range.length, "silent");
      quill.format("link", href, "user");
      quill.setSelection(range.index + range.length, 0, "silent");
    } else {
      quill.insertText(range.index, text, { link: href }, "user");
      quill.setSelection(range.index + text.length, 0, "silent");
    }
  };
};

/**
 * ``onChange`` fires only for the admin's own edits. Quill also reports a
 * change when it first loads the stored HTML and tidies it, which would mark
 * an untouched page as edited and send its body back on every save.
 */
const RichTextField = ({
  id,
  value,
  onChange,
  rtl = false,
  minHeight = 220,
  placeholder,
  readOnly = false,
  headings = [2, 3, 4],
  onLink,
}) => {
  // Quill reads the toolbar once, at mount: the object must keep its
  // identity, and the link handler reaches the latest `onLink` through a ref.
  const onLinkRef = useRef(onLink);
  onLinkRef.current = onLink;
  const levels = headings.join(",");
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [...levels.split(",").map(Number), false] }],
          ["bold", "italic"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
        ],
        handlers: {
          link(pressed) {
            if (onLinkRef.current) onLinkRef.current(inserterFor(this.quill));
            else defaultLinkHandler.call(this, pressed);
          },
        },
      },
    }),
    [levels],
  );

  return (
    <div
      id={id}
      className={`rt-rich${rtl ? " rt-rich--rtl" : ""}`}
      style={{ "--rt-rich-min": `${minHeight}px` }}
    >
      <ReactQuill
        theme="snow"
        value={value || ""}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
        // A fieldset's `disabled` does not reach Quill's contenteditable area,
        // so read-only has to be passed in explicitly.
        readOnly={readOnly}
        onChange={(html, _delta, source) => {
          if (source === "user") onChange(html);
        }}
      />
    </div>
  );
};

export default RichTextField;
